<script setup>
// ---------------------------------------------------------------------------
// How far a case gets.
//
// A chain straight down — decided, appealed, taken to court — with the
// ping-pong hanging off the side of the appeal and looping back to the start.
// That loop is the point of the picture: when the chairman quashes a decision
// the case does not move on, it goes back to the beginning, and the same office
// decides it again.
//
// The chain nests: every case at the court has been decided at least once, and
// every appeal has a first instance under it. The loop does not nest, which is
// why it is drawn beside the chain rather than as another link in it.
//
// Boxes are CSS, not SVG: a bar with a percentage width says everything the
// shape of a funnel would, and the labels can wrap.
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import { funnel } from '../cases.js'
import { filters } from '../store.js'
import { useCases } from '../useCases.js'

const props = defineProps({
    src: { type: String, required: true },
    // Pipe-separated, three of them: first instance, appeal, court.
    stepLabels: { type: String, default: '' },
    loopLabel: { type: String, default: '' },
    // Why a case comes round again — two reasons, and the label has no room.
    loopNote: { type: String, default: '' },
    // The dashboard opens with court cases filtered out, which leaves this
    // branch at nought. A nought that is a filter setting, not a finding.
    courtWarning: { type: String, default: '' },
    unitCases: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, visible, failed } = useCases(props.src)

const data = computed(() => (rows.value.length ? funnel(visible.value) : null))

const share = (count) => count / (data.value.total || 1)

const steps = computed(() => {
    const labels = props.stepLabels.split('|')

    return [data.value.total, data.value.second, data.value.court].map((count, index) => ({
        key: index,
        label: labels[index] ?? '',
        count,
        share: share(count),
        court: index === 2
    }))
})

const loop = computed(() => ({ count: data.value.third, share: share(data.value.third) }))

const hidden = computed(() => filters.soud === 'ne')

const number = (value) => value.toLocaleString('cs-CZ')
const percent = (value) =>
    value.toLocaleString('cs-CZ', { style: 'percent', maximumFractionDigits: 1 })
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <div v-else-if="data" class="casepath">
        <ol class="casepath__chain">
            <template v-for="(step, index) in steps" :key="step.key">
                <li v-if="index" class="casepath__arrow" aria-hidden="true"></li>

                <li class="casepath__step" :class="{ 'casepath__step--court': step.court }">
                    <p class="casepath__label">{{ step.label }}</p>

                    <p class="casepath__figure">
                        <span class="casepath__count">{{ number(step.count) }}</span>
                        <span class="casepath__share">{{ percent(step.share) }}</span>
                    </p>

                    <span class="casepath__meter">
                        <span
                            class="casepath__fill"
                            :class="{ 'casepath__fill--court': step.court }"
                            :style="{ width: `${step.share * 100}%` }"
                        ></span>
                    </span>

                    <p v-if="step.court && hidden" class="casepath__warning">{{ courtWarning }}</p>
                </li>
            </template>
        </ol>

        <!-- Beside the appeal, because that is where a case is sent back from,
             and joined to the chain by an arrow that returns to the top. -->
        <div class="casepath__branch">
            <div class="casepath__step casepath__step--loop">
                <p class="casepath__label">{{ loopLabel }}</p>

                <p v-if="loopNote" class="casepath__aside">{{ loopNote }}</p>

                <p class="casepath__figure">
                    <span class="casepath__count">{{ number(loop.count) }}</span>
                    <span class="casepath__share">{{ percent(loop.share) }}</span>
                </p>

                <span class="casepath__meter">
                    <span
                        class="casepath__fill casepath__fill--loop"
                        :style="{ width: `${loop.share * 100}%` }"
                    ></span>
                </span>
            </div>

            <span class="casepath__return" aria-hidden="true"></span>
        </div>
    </div>
</template>
